import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { siteTypes } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { SiteSelector } from '../components/booking/SiteSelector';
import { StayTypeSelector, STAY_OPTIONS } from '../components/booking/StayTypeSelector';
import { DateRangePicker } from '../components/booking/DateRangePicker';
import { GuestInfoForm } from '../components/booking/GuestInfoForm';
import { AddOnSelector } from '../components/booking/AddOnSelector';
import { BookingSummary } from '../components/booking/BookingSummary';
import { RvSitePicker } from '../components/booking/RvSitePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export const Booking = () => {
  const [selectedSite, setSelectedSite] = useState(null);
  const [stayType, setStayType] = useState('nightly'); // 'nightly' | 'weekly' | 'monthly'
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('2');
  const [campingType, setCampingType] = useState('tent');
  const [isProcessing, setIsProcessing] = useState(false);
  const [addOns, setAddOns] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [smsConsent, setSmsConsent] = useState(false);
  const [selectedRvSite, setSelectedRvSite] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const { toast } = useToast();

  const fetchAddOns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipment/addons`);
      setAddOns(response.data.addons || []);
    } catch (error) {
      // Add-ons are optional
    }
  }, []);

  useEffect(() => {
    fetchAddOns();
  }, [fetchAddOns]);

  // RV Sites support flat-rate weekly/monthly stays; primitive stays nightly.
  const stayTypeApplies = selectedSite?.name === 'RV Sites';
  const effectiveStayType = stayTypeApplies ? stayType : 'nightly';
  const stayOption = STAY_OPTIONS.find((o) => o.id === effectiveStayType);

  // When stay type or check-in changes (and it's a fixed-duration stay),
  // auto-fill check-out so the user can't pick an invalid range.
  useEffect(() => {
    if (!stayTypeApplies) return;
    if (effectiveStayType === 'nightly') return;
    if (!checkIn) return;
    const co = new Date(checkIn);
    co.setHours(0, 0, 0, 0);
    co.setDate(co.getDate() + stayOption.duration);
    if (!checkOut || checkOut.getTime() !== co.getTime()) {
      setCheckOut(co);
    }
  }, [effectiveStayType, checkIn, stayTypeApplies, stayOption, checkOut]);

  // When switching site type back to non-RV (nightly only), reset stay type.
  useEffect(() => {
    if (!stayTypeApplies && stayType !== 'nightly') {
      setStayType('nightly');
    }
  }, [stayTypeApplies, stayType]);

  const calculateNights = () => {
    if (checkIn && checkOut) {
      const diff = checkOut.getTime() - checkIn.getTime();
      return Math.ceil(diff / (1000 * 3600 * 24));
    }
    return 0;
  };

  const nights = calculateNights();

  // Pricing: flat rate for weekly/monthly RV stays, otherwise nightly × nights.
  let siteSubtotal = 0;
  if (selectedSite && checkIn && checkOut) {
    if (effectiveStayType === 'nightly') {
      siteSubtotal = nights * selectedSite.price;
    } else {
      siteSubtotal = stayOption.price; // flat $100 / $300
    }
  }

  const selectedAddOnsList = Object.entries(selectedAddOns)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const addon = addOns.find((a) => a.id === id);
      return addon ? { id, name: addon.name, price: addon.price, quantity: qty } : null;
    })
    .filter(Boolean);

  const addOnsTotal = selectedAddOnsList.reduce((sum, a) => sum + a.price * a.quantity, 0);

  const total = siteSubtotal + addOnsTotal;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddOnChange = (addonId, quantity) => {
    setSelectedAddOns({ ...selectedAddOns, [addonId]: parseInt(quantity) || 0 });
  };

  const handleBooking = async (e) => {
    if (e) e.preventDefault();

    if (!selectedSite || !checkIn || !checkOut) {
      toast({
        title: 'Missing information',
        description: 'Please select site type and dates',
        variant: 'destructive',
      });
      return;
    }

    if (selectedSite.name === 'RV Sites' && !selectedRvSite) {
      toast({
        title: 'Pick your RV site',
        description: 'Please choose a specific RV spot on the map before checking out.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all guest information fields',
        variant: 'destructive',
      });
      return;
    }

    if (!smsConsent) {
      toast({
        title: 'SMS consent required',
        description:
          'Please agree to receive booking-related text messages so we can confirm your reservation.',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);

    const add_ons_payload = selectedAddOnsList.map((a) => ({ id: a.id, quantity: a.quantity }));

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/bookings/create-checkout`, {
        site_type: selectedSite.name,
        stay_type: effectiveStayType,
        check_in: checkIn.toISOString(),
        check_out: checkOut.toISOString(),
        guests: parseInt(guests),
        camping_type: campingType,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        add_ons: add_ons_payload,
        sms_consent: smsConsent,
        rv_site_id: selectedSite.name === 'RV Sites' ? selectedRvSite?.id : null,
        origin_url: originUrl,
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast({
        title: 'Booking failed',
        description:
          error.response?.data?.detail || 'There was an error processing your booking. Please try again.',
        variant: 'destructive',
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      {/* Hero Section */}
      <section className="relative h-72 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(5, 8, 20, 0.8), rgba(5, 8, 20, 0.9)), url('https://images.unsplash.com/photo-1626680114529-3f6ffa002b80?auto=format&fit=crop&w=2000&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="container mx-auto px-4 z-10 text-center text-[#F4F1EA]">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Book Your Stay</h1>
          <p className="text-xl text-[#B8860B]">Choose your perfect site and start your adventure</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <SiteSelector
              sites={siteTypes}
              selectedSite={selectedSite}
              onSelect={(site) => {
                setSelectedSite(site);
                if (site?.name !== 'RV Sites') setSelectedRvSite(null);
              }}
            />
            {stayTypeApplies && (
              <StayTypeSelector stayType={stayType} onChange={setStayType} />
            )}
            <DateRangePicker
              checkIn={checkIn}
              checkOut={checkOut}
              onCheckIn={setCheckIn}
              onCheckOut={setCheckOut}
              stayType={effectiveStayType}
              lockedDuration={stayOption?.duration || null}
            />
            {selectedSite?.name === 'RV Sites' && (
              <RvSitePicker
                checkIn={checkIn}
                checkOut={checkOut}
                selectedSiteId={selectedRvSite?.id}
                onSelect={setSelectedRvSite}
              />
            )}
            <AddOnSelector addOns={addOns} selectedAddOns={selectedAddOns} onChange={handleAddOnChange} />
            <GuestInfoForm
              formData={formData}
              onInputChange={handleInputChange}
              guests={guests}
              onGuestsChange={setGuests}
              campingType={campingType}
              onCampingTypeChange={setCampingType}
              smsConsent={smsConsent}
              onSmsConsentChange={setSmsConsent}
              onSubmit={handleBooking}
            />
          </div>

          <div className="lg:col-span-1">
            <BookingSummary
              selectedSite={selectedSite}
              selectedRvSite={selectedSite?.name === 'RV Sites' ? selectedRvSite : null}
              stayType={effectiveStayType}
              checkIn={checkIn}
              checkOut={checkOut}
              nights={nights}
              siteSubtotal={siteSubtotal}
              addOnsTotal={addOnsTotal}
              total={total}
              selectedAddOnsList={selectedAddOnsList}
              onSubmit={handleBooking}
              isProcessing={isProcessing}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
